(function() {
  // Rare's protection product (verified live 2026-06-12). Variant titles encode cart-subtotal
  // bands in DOLLARS — '0T150', '250T350', ... '10000TN' (N = no upper bound) — priced
  // at ~10% of the band cap. Ported from Top's band-matching logic (RD directive 2026-06-12):
  // select the variant by matching the cart subtotal (protection item excluded) to its band.
  const PROTECTION_PRODUCT_HANDLE = 'shipping-protection-3';
  const CHECKOUT_PATH = '/checkout';
  let protectionProductPromise = null;
  let ensureProtectionPromise = null;

  function getProtectionProduct() {
    if (!protectionProductPromise) {
      protectionProductPromise = fetch(`/products/${PROTECTION_PRODUCT_HANDLE}.js`, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error('Could not load protection product.');
        return response.json();
      });
    }
    return protectionProductPromise;
  }

  function parseVariantBand(variant) {
    const match = /^(\d+)T(\d+|N)$/.exec((variant.title || '').trim());
    if (!match) return null;
    return {
      variant: variant,
      minCents: Number(match[1]) * 100,
      maxCents: match[2] === 'N' ? Infinity : Number(match[2]) * 100
    };
  }

  function selectProtectionVariant(product, subtotalCents) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const bands = variants.map(parseVariantBand).filter(Boolean).sort((a, b) => a.minCents - b.minCents);

    if (bands.length) {
      // Exact band, else the next band up (covers gaps and the open-ended top band),
      // else the highest band.
      const matched = bands.find((band) => subtotalCents >= band.minCents && subtotalCents < band.maxCents) ||
        bands.find((band) => subtotalCents < band.maxCents) ||
        bands[bands.length - 1];
      if (matched.variant.available) return matched.variant;
      // Matched band unavailable: nearest available band above it, else below.
      const index = bands.indexOf(matched);
      for (let i = index + 1; i < bands.length; i++) {
        if (bands[i].variant.available) return bands[i].variant;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (bands[i].variant.available) return bands[i].variant;
      }
    }

    return variants.find((item) => item.available) || variants[0] || null;
  }

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error('Could not load cart.');
      return response.json();
    });
  }

  function isProtectionItem(item) {
    return item.handle === PROTECTION_PRODUCT_HANDLE || item.url === `/products/${PROTECTION_PRODUCT_HANDLE}`;
  }

  function isGiftCard(item) {
    return (item.product_type || '').toLowerCase() === 'gift card';
  }

  function cartProtectableSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items
      .filter((item) => !isProtectionItem(item))
      .reduce((sum, item) => {
        const linePrice = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
        return sum + (linePrice || 0);
      }, 0);
  }

  function cartNeedsProtection(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return false;

    const hasProtection = cart.items.some(isProtectionItem);
    if (hasProtection) return false;

    const nonProtectionItems = cart.items.filter((item) => !isProtectionItem(item));
    if (!nonProtectionItems.length) return false;

    // Skip auto-add when every non-protection line item is a Gift Card.
    const allGiftCards = nonProtectionItems.every(isGiftCard);
    if (allGiftCards) return false;

    return true;
  }

  function addProtectionToCart(cart) {
    return getProtectionProduct().then((product) => {
      const variant = selectProtectionVariant(product, cartProtectableSubtotal(cart));
      if (!variant || !variant.id) throw new Error('No valid protection variant found.');
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variant.id,
            quantity: 1,
            properties: {
              '_auto_added': 'true'
            }
          }]
        })
      }).then((response) => {
        if (!response.ok) throw new Error('Could not add protection to cart.');
        return response.json();
      });
    });
  }

  function ensureProtectionInCart() {
    if (!ensureProtectionPromise) {
      ensureProtectionPromise = getCart()
        .then((cart) => {
          if (!cartNeedsProtection(cart)) return cart;
          return addProtectionToCart(cart);
        })
        .catch((error) => {
          console.error('Auto checkout protection error:', error);
          return null;
        })
        .finally(() => {
          ensureProtectionPromise = null;
        });
    }
    return ensureProtectionPromise;
  }

  function continueCheckout(submitter) {
    const form = submitter && (submitter.form || submitter.closest('form'));

    if (form) {
      form.dataset.autoProtectionReady = 'true';
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
        return;
      }
      form.submit();
      return;
    }

    window.location.href = CHECKOUT_PATH;
  }

  document.addEventListener('submit', function(event) {
    const form = event.target;
    const submitter = event.submitter;

    if (!(form instanceof HTMLFormElement)) return;
    if (!submitter || submitter.name !== 'checkout') return;

    if (form.dataset.autoProtectionReady === 'true') {
      delete form.dataset.autoProtectionReady;
      return;
    }

    event.preventDefault();
    ensureProtectionInCart().finally(function() {
      continueCheckout(submitter);
    });
  }, true);

  document.addEventListener('click', function(event) {
    const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!checkoutButton) return;

    const form = checkoutButton.form || checkoutButton.closest('form');
    if (form && form.dataset.autoProtectionReady === 'true') return;

    event.preventDefault();
    ensureProtectionInCart().finally(function() {
      continueCheckout(checkoutButton);
    });
  }, true);
})();
